import { apiFetch } from "./client";
import type { Load, LoadCollection, Page } from "./types";

export interface LoadListParams {
  limit?: number;
  offset?: number;
  day?: string;
  date_from?: string;
  date_to?: string;
  direction?: string;
}

export interface LoadCollectionInput {
  site_id?: number | null;
  trailer_id?: number | null;
  pallet_count?: number | null;
  cartons?: number | null;
  collection_reference?: string | null;
  collection_date?: string | null;
  collection_time?: string | null;
  wip_time?: string | null;
  wip_ready?: boolean;
  preloads?: string | null;
  comments?: string | null;
  weight_tonnes?: number | null;
  non_standard_pallets?: boolean;
  tallest_pallet_height?: number | null;
  shipping_request_numbers?: string | null;
  customer_reference_numbers?: string | null;
}

export interface LoadCreate {
  load_ref: string;
  customer_id: number;
  direction?: string;
  haulier_id?: number | null;
  cse_id?: number | null;
  destination_address?: string | null;
  postcode?: string | null;
  delivery_date?: string | null;
  arrival_time?: string | null;
  depot_reference_number?: string | null;
  invoice_reference?: string | null;
  collections?: LoadCollectionInput[];
}

export type { LoadCollection };

export type LoadUpdate = Partial<Omit<LoadCreate, "load_ref">> & {
  load_ref?: string;
};

export async function listLoads(params: LoadListParams = {}): Promise<Page<Load>> {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  if (params.day !== undefined) query.set("day", params.day);
  if (params.date_from !== undefined) query.set("date_from", params.date_from);
  if (params.date_to !== undefined) query.set("date_to", params.date_to);
  if (params.direction !== undefined) query.set("direction", params.direction);
  const qs = query.toString();
  return apiFetch<Page<Load>>(`/loads${qs ? `?${qs}` : ""}`);
}

export async function getLoad(id: number): Promise<Load> {
  return apiFetch<Load>(`/loads/${id}`);
}

export async function getLoadCounters(): Promise<{
  total: number;
  locked_future: number;
  unlocked_future: number;
}> {
  return apiFetch("/loads/counters");
}

export async function createLoad(data: LoadCreate): Promise<Load> {
  return apiFetch<Load>("/loads", { method: "POST", body: JSON.stringify(data) });
}

export async function updateLoad(
  id: number,
  data: LoadUpdate,
  ifMatch: string | number,
): Promise<Load> {
  return apiFetch<Load>(`/loads/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
    ifMatch,
  });
}

export async function transitionLoad(
  id: number,
  target_status: string,
  ifMatch?: string | number,
): Promise<Load> {
  return apiFetch<Load>(`/loads/${id}/transition`, {
    method: "POST",
    body: JSON.stringify({ target_status }),
    ifMatch,
  });
}

export async function deleteLoad(id: number): Promise<Load> {
  return apiFetch<Load>(`/loads/${id}`, { method: "DELETE" });
}
